import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from '.';
import { logConsole, logTransaction } from '../common/log';
import { encodeInvalidCharacters, handleThunkError } from '../common/util';
import { AttachmentAssetType, IAttachment, IAttachmentContainer } from '../models/dms/attachment.model';
import { IVehicleAttachmentRequestQueueItem, RequestQueueGroup, RequestQueueType } from '../models/dms/requestQueue.model';
import { LogType } from '../models/log.model';
import { ConfigName, updateConfigObject } from '../sqlite/common/config';
import { setIsLoadingCountState } from './appSlice';
import { addToRequestQueueStoreAsync } from './requestQueueSlice';
import { putMediaAttachments } from '../api/dms/mediaAttachment.api';

interface IVehicleAttachmentState {
  selectedVehicleAttachments: IAttachmentContainer | undefined;
  selectedVehicleAttachmentsLoading: boolean;
  adhocAttachmentsCache: IAttachment[];
  attachmentsProcessing: boolean;
}

const initialState: IVehicleAttachmentState = {
  selectedVehicleAttachments: undefined,
  selectedVehicleAttachmentsLoading: false,
  adhocAttachmentsCache: [],
  attachmentsProcessing: false,
};

/// THUNKS

export const setSelectedVehicleAttachmentsStoreAsync = createAsyncThunk<{}, IAttachmentContainer>('vehicleAttachment/setSelectedVehicleAttachmentsStoreAsync', async (attachments, { rejectWithValue, dispatch }) => {
  logTransaction(LogType.Info, `Setting selected vehicle attachments to storage`, '[vehicleAttachment] setSelectedVehicleAttachmentsStoreAsync');
  try {
    dispatch(setSelectedVehicleAttachmentsState(attachments));
    await updateConfigObject<IAttachmentContainer>(ConfigName.SelectedVehicleAttachments, attachments, false);
  } catch (err: any) {
    return handleThunkError(err, rejectWithValue);
  }
});

export const addVehicleAttachmentAsync = createAsyncThunk<
  {},
  { key: string; newAttachment: IAttachment; existingAttachments: IAttachment[]; assetType: AttachmentAssetType; repairOrderNumber: number; isAdhoc: boolean; mediaRef: string; jobType?: string; jobCode?: string; partsRequisitionId?: string }
>('vehicleAttachment/addVehicleAttachmentAsync', async ({ key, newAttachment, existingAttachments, repairOrderNumber, isAdhoc, mediaRef, jobType, jobCode, assetType, partsRequisitionId }, { rejectWithValue, dispatch, getState }) => {
  logTransaction(LogType.Info, `Adding vehicle attachment`, '[vehicleAttachment] addVehicleAttachmentAsync');
  try {
    if (isAdhoc) {
      logTransaction(LogType.Info, `Vehicle attachment is for an adhoc clocking`);
      const vehicleAttachment = {
        ...newAttachment,
      } as IAttachment;
      await dispatch(addAdhocAttachmentCacheStoreAsync(vehicleAttachment));
    } else {
      logTransaction(LogType.Info, `Vehicle attachment is for a normal clocking`);
      if (repairOrderNumber >= 1) {
        logTransaction(LogType.Info, `Attachment for RO "${repairOrderNumber}" vehicle source is "${assetType}"`);

        const attachmentData = {
          assetType,
          key,
          repairOrderNumber: repairOrderNumber.toString(),
          mediaRef,
          jobCode,
          jobType,
          attachments: [...existingAttachments, { ...newAttachment } as IAttachment] as IAttachment[],
        } as IAttachmentContainer;

        await dispatch(
          addToRequestQueueStoreAsync({
            id: new Date().getTime(),
            group: RequestQueueGroup.VehicleAttachment,
            type: RequestQueueType.VehicleAttachment,
            immediate: true,
            data: attachmentData,
          } as IVehicleAttachmentRequestQueueItem),
        );
      } else {
        throw new Error(`Labour clocking repair order number "${repairOrderNumber}" is invalid`);
      }
    }
  } catch (err: any) {
    logTransaction(LogType.Error, `Error adding vehicle attachment. ${err.message}`);
    return handleThunkError(err, rejectWithValue);
  }
});

export const postVehicleAttachmentAsync = createAsyncThunk<{}, { attachment: IAttachmentContainer }>('vehicleAttachment/postVehicleAttachmentAsync', async ({ attachment }, { rejectWithValue, dispatch, getState }) => {
  logTransaction(LogType.Info, `POST vehicle attachment to database, repairOrderNumber: ${attachment.key}`, '[vehicleAttachment] postVehicleAttachmentAsync');
  try {
    dispatch(setIsLoadingCountState(1));
    const state = getState() as RootState;
    const { selectedVehicleAttachments } = state.vehicleAttachments;
    const { labourClocking } = state.technicianClocking;

    let sanitizedKey: string | undefined;
    if (attachment.key !== undefined) {
      sanitizedKey = encodeInvalidCharacters(attachment.key);
    } else {
      throw new Error('attachment key is undefined');
    }

    let sanitizedJobCode: string | undefined;
    if (attachment.jobCode !== undefined) {
      sanitizedJobCode = encodeInvalidCharacters(attachment.jobCode);
    }

    let sanitizedJobType: string | undefined;
    if (attachment.jobType !== undefined) {
      sanitizedJobType = encodeInvalidCharacters(attachment.jobType);
    }

    const attachmentData = attachment.attachments as IAttachment[];
    const attachmentResponseData = await putMediaAttachments(attachment.assetType, sanitizedKey, attachmentData, attachment.repairOrderNumber, sanitizedJobCode, sanitizedJobType, attachment.mediaRef);
    // After PUT, update the attachments in the store by replacing the store with the response from the server
    if ((selectedVehicleAttachments?.repairOrderNumber || '-1') === (labourClocking?.repairOrder.repairOrderNumber || -1).toString()) {
      const newAttachmentContainer = {
        ...selectedVehicleAttachments,
        attachments: [...attachmentResponseData.attachments],
      } as IAttachmentContainer;

      await dispatch(setSelectedVehicleAttachmentsStoreAsync(newAttachmentContainer));
    }

    dispatch(setIsLoadingCountState(-1));
    return attachmentResponseData;
  } catch (err: any) {
    return handleThunkError(err, rejectWithValue, setIsLoadingCountState(-1));
  }
});

export const setAdhocAttachmentCacheStoreAsync = createAsyncThunk<{}, IAttachment[]>('vehicleAttachment/setAdhocAttachmentCacheStoreAsync', async (attachments, { rejectWithValue, dispatch, getState }) => {
  logTransaction(LogType.Info, `Setting adhoc attachments cache to storage`, '[vehicleAttachment] setAdhocAttachmentCacheStoreAsync');
  try {
    dispatch(setAdhocAttachmentsCacheState(attachments));
    await updateConfigObject<IAttachment[]>(ConfigName.AdhocVehicleAttachmentsCache, attachments, false);
  } catch (err: any) {
    return handleThunkError(err, rejectWithValue, setIsLoadingCountState(-1));
  }
});

export const addAdhocAttachmentCacheStoreAsync = createAsyncThunk<{}, IAttachment>('vehicleAttachment/addAdhocAttachmentCacheStoreAsync', async (attachment, { rejectWithValue, dispatch, getState }) => {
  logTransaction(LogType.Info, `Adding adhoc attachment cache to storage`, '[vehicleAttachment] addAdhocAttachmentCacheStoreAsync');
  try {
    const newAttachments = [...(getState() as RootState).vehicleAttachments.adhocAttachmentsCache, attachment];
    dispatch(setAdhocAttachmentsCacheState(newAttachments));
    await updateConfigObject<IAttachment[]>(ConfigName.AdhocVehicleAttachmentsCache, newAttachments, false);
  } catch (err: any) {
    return handleThunkError(err, rejectWithValue, setIsLoadingCountState(-1));
  }
});

/// SLICE

export const vehicleAttachmentSlice = createSlice({
  name: 'vehicleAttachment',
  initialState,
  reducers: {
    setSelectedVehicleAttachmentsState: (state, action: PayloadAction<IAttachmentContainer>) => {
      logConsole(LogType.Info, '[vehicleAttachment] setSelectedVehicleAttachmentsState');
      state.selectedVehicleAttachments = action.payload;
    },
    setSelectedVehicleAttachmentsLoadingState: (state, action: PayloadAction<boolean>) => {
      logConsole(LogType.Info, `[vehicleAttachment] setSelectedVehicleAttachmentsLoadingState: ${action.payload}`);
      state.selectedVehicleAttachmentsLoading = action.payload;
    },
    setAdhocAttachmentsCacheState: (state, action: PayloadAction<IAttachment[]>) => {
      logConsole(LogType.Info, `[vehicleAttachment] setAdhocAttachmentsCacheState`);
      state.adhocAttachmentsCache = action.payload;
    },
    setAttachmentsProcessingState: (state, action: PayloadAction<boolean>) => {
      logConsole(LogType.Info, `[vehicleAttachment] setAttachmentsProcessingState`);
      state.attachmentsProcessing = action.payload;
    },
  },
});

/// REDUCERS

export const { setSelectedVehicleAttachmentsState, setSelectedVehicleAttachmentsLoadingState, setAdhocAttachmentsCacheState, setAttachmentsProcessingState } = vehicleAttachmentSlice.actions;

/// CUSTOM SELECTORS

export default vehicleAttachmentSlice.reducer;
